import React, { useEffect, useRef, useState } from 'react';

import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  AppBar,
  Badge,
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Button as MuiButton,
  Toolbar,
  Typography,
} from '@mui/material';

import Notifications from 'components/Layout/Notifications';
import NotificationTooltip from 'components/Layout/Notifications/NotificationTooltip';
import MobileMenu from 'components/MobileMenu';
import RenderMenuContent from 'components/RenderMenuContent';
import { useMenu } from 'global/MenuContext';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useDynamicLogo } from 'hooks/useDynamicLogo';
import { useNotificationTooltip } from 'hooks/useNotificationTooltip';
import { useViewportHeight } from 'hooks/useViewportHeight';
import { useMeetingRooms } from 'pages/Rooms/hooks/useMeetingRooms';
import { MeetingRoomExtras, MenuItem, isMeetingRoomExtras } from 'types/menuTypes';

let backgroundImage: string | undefined;
try {
  backgroundImage = require('assets/images/header/header-background.png');
} catch {
  backgroundImage = undefined;
}

const LogoHeaderImage = styled.img`
  max-height: 4.5rem;
`;

interface LayoutMobileProps {
  children: React.ReactNode;
}

export const LayoutMobile = ({ children }: LayoutMobileProps) => {
  const { currentLevel, firstLevelMenu, secondLevelMenu, thirdLevelMenu, selectMenuByDocumentId } =
    useMenuSelection();
  const { menu } = useMenu();
  const { logo, isLoading: logoLoading } = useDynamicLogo();
  const { collections } = useMeetingRooms();
  const { isSafari, isMobile } = useViewportHeight();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [headerTitle, setHeaderTitle] = useState('');
  const location = useLocation();
  const { roomId } = useParams();
  const lastSegment = location.pathname.split('/').filter(Boolean).pop();
  const roomIdentifier = roomId || lastSegment;
  const [notifTop, setNotifTop] = useState(80); // posição inicial em px
  const [dragging, setDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [notifStartTop, setNotifStartTop] = useState(80);
  const [openNotifications, setOpenNotifications] = useState(false);
  const [newNotifications, setNewNotifications] = useState(0);
  const [notificationsDrawerOpen, setNotificationsDrawerOpen] = useState(false);

  // Referência para o botão flutuante de notificações
  const floatingNotificationRef = useRef<HTMLDivElement>(null);

  // Hook para gerenciar tooltip de notificações
  const { showTooltip, tooltipNotification, handleTooltipClose } = useNotificationTooltip();

  // Funções de drag
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    setDragging(true);
    setDragStartY('touches' in e ? e.touches[0].clientY : e.clientY);
    setNotifStartTop(notifTop);
    e.stopPropagation();
  };
  const handleDrag = (e: MouseEvent | TouchEvent) => {
    if (!dragging) return;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
    let newTop = notifStartTop + (clientY - dragStartY);
    // Limites para não sair da tela
    newTop = Math.max(56, Math.min(window.innerHeight - 80, newTop));
    setNotifTop(newTop);
  };
  const handleDragEnd = () => setDragging(false);

  useEffect(() => {
    if (dragging) {
      const move = (e: any) => handleDrag(e);
      const up = () => handleDragEnd();
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
      window.addEventListener('touchmove', move);
      window.addEventListener('touchend', up);
      return () => {
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', up);
        window.removeEventListener('touchmove', move);
        window.removeEventListener('touchend', up);
      };
    }
  }, [dragging, dragStartY, notifStartTop]);

  // Encontra o item ativo baseado no path atual
  const findActiveItem = (items: MenuItem[], path: string): MenuItem | undefined => {
    for (const item of items) {
      if (item.path === path) {
        return item;
      }
      if (item.subMenu) {
        const found = findActiveItem(item.subMenu, path);
        if (found) return found;
      }
    }
    return undefined;
  };

  // Encontra o item ativo no menu atual
  const currentItem = findActiveItem(menu, location.pathname);

  // Detecta se é uma sala de reunião
  const isMeetingRoom = currentItem?.feature?.dynamic_component === 'MEETING_ROOM';
  const isMeetingRoomRoom = currentItem?.feature?.dynamic_component === 'MEETING_ROOM_ROOM';
  // Encontra a sala correspondente
  const currentRoom =
    (isMeetingRoom || isMeetingRoomRoom) && collections
      ? collections
          .flatMap(collection => collection.rooms)
          .find(
            room =>
              room.id?.toString() === roomIdentifier ||
              room.room_data?.id?.toString() === roomIdentifier ||
              room.internal_id?.toString() === roomIdentifier
          )
      : null;

  // Pega a imagem de background se existir
  const meetingBackground =
    currentRoom?.background?.url ||
    (currentItem?.feature && isMeetingRoomExtras(currentItem?.feature?.extras ?? null)
      ? (currentItem.feature.extras as MeetingRoomExtras).background?.url
      : undefined);

  // Função para pegar o título do header
  const getHeaderTitle = () => {
    // Se estamos em uma sala de reunião, mostra o nome da sala
    if ((isMeetingRoom || isMeetingRoomRoom) && currentRoom && secondLevelMenu) {
      //return currentRoom.title || currentRoom.room_data?.title || '';
      return secondLevelMenu.label || '';
    }

    // Se estamos em um menu de terceiro nível
    if (currentLevel === 'third' && thirdLevelMenu && secondLevelMenu) {
      return secondLevelMenu.label || '';
    }

    // Se estamos em um menu de segundo nível
    if (currentLevel === 'second' && secondLevelMenu) {
      return secondLevelMenu.label || '';
    }

    // Se estamos em um menu de primeiro nível
    if (currentLevel === 'first' && firstLevelMenu) {
      return firstLevelMenu.label || '';
    }

    return '';
  };

  // Efeito para sincronizar o título do header
  useEffect(() => {
    const newTitle = getHeaderTitle();
    setHeaderTitle(newTitle);
  }, [currentLevel, firstLevelMenu, secondLevelMenu, thirdLevelMenu, currentRoom]);

  const handleThirdLevelMenuClick = (selectedItem: MenuItem) => {
    if (selectedItem.feature?.documentId) {
      selectMenuByDocumentId(selectedItem.feature.documentId, true, undefined, selectedItem.path);
    } else if (selectedItem.extras && isMeetingRoomExtras(selectedItem.extras ?? null)) {
      selectMenuByDocumentId(
        (selectedItem.extras as MeetingRoomExtras).internal_id || '',
        true,
        undefined,
        selectedItem.path
      );
    }
    setDrawerOpen(false);
  };

  // Encontra os itens do terceiro nível
  const getThirdLevelItems = () => {
    if (currentLevel === 'third' && secondLevelMenu?.subMenu) {
      return secondLevelMenu.subMenu;
    }
    return [];
  };

  const thirdLevelItems = getThirdLevelItems();

  // Função para renderizar o conteúdo baseado no nível atual e menu ativo
  const renderContent = () => {
    // Para outros tipos de conteúdo, usa o RenderMenuContent baseado no nível atual
    switch (currentLevel) {
      case 'first':
        console.log('[LayoutMobile] Renderizando primeiro nível:', firstLevelMenu?.label);
        return <RenderMenuContent menu={firstLevelMenu}>{children}</RenderMenuContent>;
      case 'second':
        console.log('[LayoutMobile] Renderizando segundo nível:', secondLevelMenu?.label);
        return <RenderMenuContent menu={secondLevelMenu}>{children}</RenderMenuContent>;
      case 'third':
        console.log('[LayoutMobile] Renderizando terceiro nível:', thirdLevelMenu?.label);
        return <RenderMenuContent menu={thirdLevelMenu}>{children}</RenderMenuContent>;
      default:
        console.log('[LayoutMobile] Renderizando children padrão');
        return children;
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        color="default"
        elevation={1}
        sx={{
          backgroundColor: theme => theme.palette.background.paper,
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
          backgroundSize: backgroundImage ? 'cover' : undefined,
          backgroundPosition: backgroundImage ? 'center' : undefined,
          backgroundRepeat: backgroundImage ? 'no-repeat' : undefined,
          height: 56,
          justifyContent: 'center',
        }}
      >
        <Toolbar
          sx={{
            px: 2,
            minHeight: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!logoLoading && logo && <LogoHeaderImage src={logo} alt="Logo" />}
          </Box>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ ml: 2, flex: 1, textAlign: 'right', color: 'text.primary', fontSize: '1.2rem' }}
          >
            {headerTitle}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        className="mobile-viewport-height"
        sx={{
          bgcolor: 'background.default',
          backgroundImage: meetingBackground ? `url(${meetingBackground})` : undefined,
          backgroundSize: meetingBackground ? 'cover' : undefined,
          backgroundPosition: meetingBackground ? 'center' : undefined,
          backgroundRepeat: meetingBackground ? 'no-repeat' : undefined,
          mt: meetingBackground ? 7 : 8,
        }}
      >
        {/* Botão para abrir Drawer de terceiro nível, se houver mais de um item */}
        {thirdLevelItems.length > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <MuiButton
              variant="outlined"
              fullWidth
              endIcon={<ArrowDropDownIcon sx={{ color: 'text.secondary', fontSize: 28 }} />}
              sx={{
                bgcolor: 'background.paper',
                color: 'text.primary',
                borderRadius: 2,
                my: 1,
                width: '95%',
                fontWeight: 'normal',
                fontSize: '1rem',
                justifyContent: 'space-between',
                border: theme => `1.5px solid ${theme.palette.divider}`,
                boxShadow: 'none',
                textTransform: 'none',
                px: 2,
                py: 1.2,
                height: 48,
                '&:hover': {
                  bgcolor: 'background.paper',
                  borderColor: theme => theme.palette.primary.main,
                },
              }}
              onClick={() => setDrawerOpen(true)}
            >
              {currentItem?.label || thirdLevelItems[0].label}
            </MuiButton>
            <Drawer
              anchor="bottom"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              PaperProps={{
                sx: {
                  borderTopLeftRadius: 16,
                  borderTopRightRadius: 16,
                  minHeight: '30vh',
                  maxHeight: 'auto',
                  bgcolor: 'background.default',
                },
              }}
            >
              <Box sx={{ p: 1 }}>
                <Divider sx={{ mb: 1 }} />
                <List>
                  {thirdLevelItems.map(item => (
                    <ListItemButton
                      key={
                        item.feature?.documentId ||
                        (isMeetingRoomExtras(item.extras ?? null)
                          ? (item.extras as any).internal_id
                          : undefined) ||
                        item.path
                      }
                      selected={item.path === currentItem?.path}
                      disabled={item.isActive === false}
                      onClick={() => handleThirdLevelMenuClick(item)}
                      sx={{ borderRadius: 2, mb: 1 }}
                    >
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            </Drawer>
          </Box>
        )}
        <Box className="mobile-content-area" sx={{ mx: 1 }}>
          {renderContent()}
        </Box>
      </Box>
      <MobileMenu />
      {/* Botão flutuante de notificações */}
      <Box
        ref={floatingNotificationRef}
        sx={{
          position: 'fixed',
          top: notifTop,
          right: 0,
          zIndex: 2000,
          cursor: dragging ? 'grabbing' : 'grab',
          transition: dragging ? 'none' : 'top 0.2s',
          userSelect: 'none',
          display: notificationsDrawerOpen ? 'none' : 'flex',
        }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <Badge
          badgeContent={newNotifications}
          color="error"
          max={9}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.5rem',
              minWidth: 12,
              height: 12,
              right: 12,
              top: 24,
            },
          }}
        >
          <Box
            onClick={e => {
              e.stopPropagation();
              setOpenNotifications(true);
            }}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '32% 0 0 32%',
              boxShadow: 3,
              px: 0.75,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <NotificationsIcon fontSize="large" sx={{ color: 'primary.main' }} />
          </Box>
        </Badge>
      </Box>

      {/* Tooltip de nova notificação para mobile */}
      {tooltipNotification && (
        <NotificationTooltip
          notification={tooltipNotification}
          isVisible={showTooltip}
          onClose={handleTooltipClose}
          position="mobile"
          anchorEl={floatingNotificationRef.current}
        />
      )}

      <Notifications
        open={openNotifications}
        onClose={() => setOpenNotifications(false)}
        onNewNotification={setNewNotifications}
        onOpenChange={setNotificationsDrawerOpen}
      />
    </>
  );
};

export default LayoutMobile;
